import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Single-select with a search box. Options are `{ value, label }`.
 * The search box appears once the list is long enough to need one.
 */
const SEARCH_THRESHOLD = 5;

export default function SearchableSelect({
  id,
  label,
  required = false,
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  emptyText,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const showSearch = options.length > SEARCH_THRESHOLD;

  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, term]);

  // Close when the click lands outside the control.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open && showSearch) searchRef.current?.focus();
    if (!open) setTerm("");
  }, [open, showSearch]);

  const pick = (option) => {
    onChange(option.value);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      {label && (
        <span className="field-label">
          {label} {required && <span className="text-danger-text">*</span>}
        </span>
      )}

      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`field-input flex items-center justify-between gap-2 text-start ${
          error ? "border-danger" : ""
        } ${open ? "border-accent ring-2 ring-accent-line" : ""}`}
      >
        <span className={`truncate ${selected ? "" : "text-ink-2"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-ink-muted transition ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-line bg-surface p-1 shadow-lg">
          {showSearch && (
            <input
              ref={searchRef}
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={t("common.search")}
              className="mb-1 w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent"
            />
          )}

          <ul role="listbox" className="max-h-56 overflow-y-auto">
            {options.length === 0 && (
              <li className="px-3 py-2 text-sm text-ink-2">{emptyText ?? t("common.none")}</li>
            )}

            {options.length > 0 && visible.length === 0 && (
              <li className="px-3 py-2 text-sm text-ink-2">{t("common.noMatches")}</li>
            )}

            {visible.map((option) => {
              const isSelected = String(option.value) === String(value);
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => pick(option)}
                    className={`w-full rounded-md px-3 py-2 text-start text-sm transition hover:bg-raised ${
                      isSelected ? "bg-accent-wash font-medium text-accent-text" : ""
                    }`}
                  >
                    {option.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {error && <p className="field-error">{error}</p>}
    </div>
  );
}
